import { type CreateMaintenanceCustomerRequest } from '@gb-ticket/shared-types';
import { Controller, useFormContext } from 'react-hook-form';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

function FieldError({ message }: { message?: string }) {
  if (!message) {
    return null;
  }
  return (
    <p className="text-sm text-destructive" role="alert">
      {message}
    </p>
  );
}

export function MaintenanceCustomerStammdatenFields() {
  const { register, control, formState } = useFormContext<CreateMaintenanceCustomerRequest>();
  const errors = formState.errors;

  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold">Stammdaten</h2>
      <div className="space-y-2">
        <Label htmlFor="cust-name">Name</Label>
        <Input id="cust-name" className="min-h-11" {...register('name')} />
        <FieldError message={errors.name?.message} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="cust-no">Kundennummer (optional)</Label>
        <Input id="cust-no" className="min-h-11" {...register('customerNumber')} />
        <FieldError message={errors.customerNumber?.message} />
      </div>
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor="cust-active">Aktiv</Label>
        <Controller
          name="isActive"
          control={control}
          render={({ field }) => (
            <Switch
              id="cust-active"
              checked={field.value}
              onCheckedChange={field.onChange}
              aria-label="Kunde ist aktiv"
            />
          )}
        />
      </div>
    </div>
  );
}

export function MaintenanceCustomerAddressFields() {
  const { register, formState } = useFormContext<CreateMaintenanceCustomerRequest>();
  const errors = formState.errors;

  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold">Adresse</h2>
      <div className="space-y-2">
        <Label htmlFor="cust-line1">Straße und Hausnummer</Label>
        <Input id="cust-line1" className="min-h-11" {...register('address.line1')} />
        <FieldError message={errors.address?.line1?.message} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="cust-line2">Adresszeile 2 (optional)</Label>
        <Input id="cust-line2" className="min-h-11" {...register('address.line2')} />
        <FieldError message={errors.address?.line2?.message} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="cust-plz">PLZ</Label>
          <Input id="cust-plz" className="min-h-11" {...register('address.postalCode')} />
          <FieldError message={errors.address?.postalCode?.message} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cust-city">Ort</Label>
          <Input id="cust-city" className="min-h-11" {...register('address.city')} />
          <FieldError message={errors.address?.city?.message} />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="cust-country">Land</Label>
        <Input
          id="cust-country"
          className="min-h-11 max-w-[6rem] uppercase"
          maxLength={2}
          {...register('address.country')}
        />
        <p className="text-xs text-muted-foreground">
          Zweistelliges Länderkürzel nach ISO 3166-1, z. B. DE.
        </p>
        <FieldError message={errors.address?.country?.message} />
      </div>
    </div>
  );
}

export function MaintenanceCustomerContactFields() {
  const { register, formState } = useFormContext<CreateMaintenanceCustomerRequest>();
  const errors = formState.errors;

  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold">Ansprechpartner</h2>
      <div className="space-y-2">
        <Label htmlFor="cust-contact-name">Name (optional)</Label>
        <Input id="cust-contact-name" className="min-h-11" {...register('contactName')} />
        <FieldError message={errors.contactName?.message} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="cust-contact-email">E-Mail (optional)</Label>
        <Input
          id="cust-contact-email"
          type="email"
          className="min-h-11"
          {...register('contactEmail')}
        />
        <FieldError message={errors.contactEmail?.message} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="cust-contact-phone">Telefon (optional)</Label>
        <Input
          id="cust-contact-phone"
          type="tel"
          className="min-h-11"
          {...register('contactPhone')}
        />
        <FieldError message={errors.contactPhone?.message} />
      </div>
    </div>
  );
}
